import { useRouter } from "next/router";
import Link from "next/link";
import { Container } from "@/components/ui/Container";
import { cn } from "@/lib/utils";
import { SITE } from "@/lib/site";
import { industries, industryPages, services } from "@/lib/data";
import { locationList } from "@/lib/locations";
import { directoryHref } from "@/lib/construction/places";

const liveIndustries = industries.filter((i) => i.slug in industryPages);

const areas = locationList.map((l) => ({
  label: l.city,
  href: `/locations/${l.slug}`,
}));

export function Footer() {
  return (
    <footer className="bg-canvas pt-20 pb-12 text-sm text-ink-muted" aria-label="Site footer">
      <Container>
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-12">
          <div className="sm:col-span-2 lg:col-span-4">
            <Link href="/" className="flex items-center gap-2.5" aria-label={SITE.brand}>
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-ink text-accent">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M3 21V9l9-5 9 5v12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M9 21v-7h6v7" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                </svg>
              </span>
              <span className="font-display text-base font-bold tracking-tight text-ink">
                ConstructionWebDesign<span className="text-ink/40">.uk</span>
              </span>
            </Link>
            <p className="mt-5 max-w-sm text-ink-muted">{SITE.tagline}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="rounded-full border border-ink/10 bg-white px-3 py-1.5 text-xs font-semibold tracking-tight text-ink-muted">
                £1,000 fixed price
              </span>
              <span className="rounded-full border border-ink/10 bg-white px-3 py-1.5 text-xs font-semibold tracking-tight text-ink-muted">
                Live in 7 days
              </span>
              <span className="rounded-full border border-ink/10 bg-white px-3 py-1.5 text-xs font-semibold tracking-tight text-ink-muted">
                You own it
              </span>
            </div>
            <a
              href="https://landdding.com/l/construction-website-design-agency-uk--constructionwebdesign-rx5k1"
              target="_blank"
              rel="noopener"
              className="mt-6 inline-block"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="https://landdding.com/api/badge/construction-website-design-agency-uk--constructionwebdesign-rx5k1?theme=dark&variant=default"
                alt="Featured on Landdding — Construction Website Design Agency UK | ConstructionWebDesign"
                width={200}
                style={{ height: "auto", display: "inline-block" }}
              />
            </a>
          </div>

          <FooterColumn title="Services" className="lg:col-span-3">
            {services
              .filter((s) => s.id !== "construction-lead-generation")
              .map((s) => (
                <FooterLink key={s.id} href={s.href ?? "/pricing"}>
                  {s.name}
                </FooterLink>
              ))}
            <FooterLink href="/pricing">Pricing</FooterLink>
            <FooterLink href="/get-website">Get a website</FooterLink>
          </FooterColumn>

          <FooterColumn title="Company" className="lg:col-span-2">
            <FooterLink href="/#demos">Live demos</FooterLink>
            <FooterLink href="/blog">Blog</FooterLink>
            <FooterLink href="/#industries">Industries</FooterLink>
            <FooterLink href="/locations">Areas we cover</FooterLink>
            <FooterLink href={directoryHref()}>Construction companies directory</FooterLink>
            <FooterLink href="/#faq">FAQ</FooterLink>
            <FooterLink href="/contact">Contact</FooterLink>
          </FooterColumn>

          <FooterColumn
            title="Areas we cover"
            className="lg:col-span-3"
            listClassName="grid grid-cols-2 gap-x-6 gap-y-2.5"
          >
            {areas.map((a) => (
              <FooterLink key={a.href} href={a.href}>
                {a.label}
              </FooterLink>
            ))}
            <FooterLink href="/locations">UK-wide</FooterLink>
          </FooterColumn>
        </div>

        <div className="mt-14 border-t border-ink/[0.08] pt-10">
          <h3 className="text-[11px] font-semibold uppercase tracking-caps text-ink">
            Industries we build for
          </h3>
          <ul className="mt-5 flex flex-wrap gap-2">
            {liveIndustries.map((i) => (
              <li key={i.slug}>
                <Link
                  href={`/industries/${i.slug}`}
                  className="inline-block rounded-full border border-ink/10 bg-white px-3 py-1.5 text-xs font-medium text-ink-muted transition-colors duration-200 hover:border-ink/25 hover:text-ink"
                >
                  {i.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-6 border-t border-ink/[0.08] pt-8 md:flex-row md:items-center">
          <p className="text-xs text-ink-muted">
            © {new Date().getFullYear()} {SITE.name} · All rights reserved ·
            Web design and SEO for the UK construction industry.
          </p>
          <nav aria-label="Legal and contact" className="flex flex-wrap items-center gap-5 text-xs">
            <a href={`mailto:${SITE.email}`} className="transition-colors hover:text-ink">
              {SITE.email}
            </a>
            <a
              href={SITE.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-ink"
            >
              LinkedIn
            </a>
            <Link href="/privacy" className="transition-colors hover:text-ink">
              Privacy
            </Link>
            <Link href="/terms" className="transition-colors hover:text-ink">
              Terms
            </Link>
          </nav>
        </div>
      </Container>
    </footer>
  );
}

function FooterColumn({
  title,
  children,
  className = "",
  listClassName = "space-y-2.5",
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  listClassName?: string;
}) {
  return (
    <div className={className}>
      <h3 className="text-[11px] font-semibold uppercase tracking-caps text-ink">
        {title}
      </h3>
      <ul className={cn("mt-4", listClassName)}>{children}</ul>
    </div>
  );
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  const { pathname } = useRouter();
  const hrefPath = href.split("#")[0] || "/";
  const isActive = hrefPath !== "/" && pathname === hrefPath;

  return (
    <li>
      <Link
        href={href}
        className={cn(
          "text-ink-muted transition-colors duration-200 hover:text-ink",
          isActive && "text-ink underline decoration-yellow-400 decoration-2 underline-offset-2"
        )}
      >
        {children}
      </Link>
    </li>
  );
}
